import { EvaChartStates } from "types/factory";

import { DataEnum } from "models";
import { ChartSeries } from "models/model";

import { checkSeriesDataXDateGreaterThanMin } from "./utils/checkSeriesDataXDateGreaterThanMin";
import { checkSeriesDataXDateLessThanMax } from "./utils/checkSeriesDataXDateLessThanMax";
import { checkSeriesDataYValueGreaterThanMax } from "./utils/checkSeriesDataYValueGreaterThanMax";
import { convertToNumber } from "./utils/convertToNumber";
import { getYMaxProductLock } from "./utils/getYMaxProductLock";
import { getYMinProductLock } from "./utils/getYMinProductLock";

export function totalRateDateLocks(states: Partial<EvaChartStates>) {
  const { locks, product, layout } = states;

  let lockedXMinType = undefined;
  let lockedXMaxType = undefined;
  let lockedYMinType = undefined;
  let lockedYMaxType = undefined;

  const series = [
    {
      x: layout.xAxis.data
    }
  ] as ChartSeries[];

  const isUserXMinLock = locks?.xMin?.type !== DataEnum.DefaultDate && locks?.xMin?.value;
  const isUserXMaxLock = locks?.xMax?.type !== DataEnum.DefaultDate && locks?.xMax?.value;

  const lockedXMin =
    locks?.xMin?.type === DataEnum.Reset
      ? undefined
      : isUserXMaxLock || isUserXMinLock //save the lock value if the user inputs a lock for either min or max
      ? locks?.xMin?.value
      : checkSeriesDataXDateGreaterThanMin("2000", series);

  const lockedXMax =
    locks?.xMax?.type === DataEnum.Reset
      ? undefined
      : isUserXMaxLock || isUserXMinLock //save the lock value if the user inputs a lock for either min or max
      ? locks?.xMax?.value
      : checkSeriesDataXDateLessThanMax("2050", series);

  const isUserYMinLock =
    locks?.yMin?.type !== DataEnum.DefaultProductNumber &&
    locks?.yMin?.value !== undefined;
  const isUserYMaxLock =
    locks?.yMax?.type !== DataEnum.DefaultProductNumber &&
    locks?.yMax?.value !== undefined;

  const lockedYMin =
    locks?.yMin?.type === DataEnum.Reset
      ? undefined
      : isUserYMinLock
      ? locks?.yMin?.value
      : getYMinProductLock(product);

  const lockedYMax =
    locks?.yMax?.type === DataEnum.Reset
      ? undefined
      : isUserYMaxLock
      ? locks?.yMax?.value
      : checkSeriesDataYValueGreaterThanMax(getYMaxProductLock(product), series, product);

  // If initial lock was undefined, chart has just been switched to and the lock has been set
  // meaning the data has gone beyond the bounds of the lock
  // We will use DefaultDate to track this state until the lock is reset
  if ((!locks?.xMin?.value && lockedXMin) || locks?.xMin?.type === DataEnum.DefaultDate) {
    lockedXMinType = DataEnum.DefaultDate;
  }

  if ((!locks?.xMax?.value && lockedXMax) || locks?.xMax?.type === DataEnum.DefaultDate) {
    lockedXMaxType = DataEnum.DefaultDate;
  }

  if (!isUserYMaxLock && lockedYMax) {
    lockedYMaxType = DataEnum.DefaultProductNumber;
  }
  if (!isUserYMinLock && lockedYMin) {
    lockedYMinType = DataEnum.DefaultProductNumber;
  }

  const isUserY2MinLock =
    locks?.y2Min?.type !== DataEnum.Reset && locks?.y2Min?.value !== undefined;
  const isUserY2MaxLock =
    locks?.y2Max?.type !== DataEnum.Reset && locks?.y2Max?.value !== undefined;

  const lockedY2Min =
    locks?.y2Min?.type === DataEnum.Reset
      ? undefined
      : isUserY2MinLock
      ? locks?.y2Min?.value
      : 0;

  const lockedY2Max =
    locks?.y2Max?.type === DataEnum.Reset
      ? undefined
      : isUserY2MaxLock
      ? locks?.y2Max?.value
      : undefined;

  const lockedY2MinType =
    !isUserY2MinLock && lockedY2Min !== undefined
      ? DataEnum.DefaultProductNumber
      : undefined;

  const lockedY2MaxType =
    !isUserY2MaxLock && lockedY2Max !== undefined
      ? DataEnum.DefaultProductNumber
      : undefined;

  return {
    lockedXMin,
    lockedXMax,
    lockedYMin: convertToNumber(lockedYMin),
    lockedYMax: convertToNumber(lockedYMax),
    lockedXMinType,
    lockedXMaxType,
    lockedYMinType,
    lockedYMaxType,
    lockedY2Min: convertToNumber(lockedY2Min),
    lockedY2Max: convertToNumber(lockedY2Max),
    lockedY2MinType,
    lockedY2MaxType
  };
}
